import React, { useCallback, useEffect, useMemo, useState } from 'react';
import paymentsApiService from '../../../services/paymentsApi';
import venueDashboardApiService from '../../../services/venueDashboardApi';
import { buildStripeConnectCallbackUrl } from '../../../utils/stripeConnectNavigation';
import {
  clearCachedStripeConnectStatus,
  formatStripeConnectLoadError,
  getCachedStripeConnectStatus,
  loadVenueStripeConnectStatus
} from '../../../utils/stripeConnectStatus';

const statusFromConnect = (status) => {
  if (!status) return 'not_connected';
  const onboarding = String(status.stripe_onboarding_status || '').toLowerCase();
  if (onboarding === 'active') return 'active';
  if (onboarding === 'restricted') return 'restricted';
  if (onboarding === 'pending') return 'pending';
  if (onboarding === 'not_started') return 'not_connected';
  if (status.charges_enabled && status.payouts_enabled) return 'active';
  if (status.account_id || status.stripe_account_id) return 'pending';
  return 'not_connected';
};

const statusLabelMap = {
  not_connected: 'Not connected',
  pending: 'Action required',
  restricted: 'Restricted',
  active: 'Ready for payouts'
};

const readRequirementsDue = (requirementsDue) => {
  if (!requirementsDue) return [];
  if (Array.isArray(requirementsDue)) return requirementsDue;
  if (Array.isArray(requirementsDue.currently_due)) return requirementsDue.currently_due;
  return [];
};

const StripeConnectCard = ({ venueId, venueDetails = null }) => {
  const hasBackendToken = () => Boolean(localStorage.getItem('access_token'));
  const isAuthErrorMessage = (message = '') => {
    const text = String(message || '').toLowerCase();
    return (
      text.includes('not authorised') ||
      text.includes('not authorized') ||
      text.includes('not authenticated') ||
      text.includes('log in again') ||
      text.includes('unauthor') ||
      text.includes('401')
    );
  };
  const isNetworkErrorMessage = (message = '') => {
    const text = String(message || '').toLowerCase();
    return (
      text.includes('failed to fetch') ||
      text.includes('networkerror') ||
      text.includes('network/cors') ||
      text.includes('unable to reach payments api')
    );
  };
  const getAuthErrorMessage = () =>
    'Your Occasia login session is missing or expired. Sign out, log in again from the venue dashboard, then retry Connect Stripe.';
  const getNetworkErrorMessage = () =>
    'We couldn’t reach the payment service. Check your connection and try again.';

  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [connectStatus, setConnectStatus] = useState(null);

  const summaryState = useMemo(
    () => statusFromConnect(connectStatus),
    [connectStatus]
  );
  const canManagePayments = hasBackendToken();
  const requirementsDue = useMemo(
    () => readRequirementsDue(connectStatus?.requirements_due),
    [connectStatus]
  );
  const connectReturnUrl =
    typeof window !== 'undefined' && window.location
      ? buildStripeConnectCallbackUrl(window.location.origin)
      : undefined;

  const loadStatus = useCallback(async ({ forceRefresh = false } = {}) => {
    if (!venueId) return;
    if (!hasBackendToken()) {
      setError(getAuthErrorMessage());
      return;
    }

    const cacheKey = String(venueId);
    if (!forceRefresh) {
      const cached = getCachedStripeConnectStatus(cacheKey);
      if (cached) {
        setConnectStatus(cached.status);
        setInfo(cached.info || '');
        setError('');
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    setError('');
    if (forceRefresh) {
      setInfo('');
    }
    try {
      const { status, usedVenueFallback, info: cachedInfo } = await loadVenueStripeConnectStatus({
        venueId,
        venueDetails,
        fetchVenueDetails: (id) => venueDashboardApiService.getVenueDetails(id),
        fetchConnectStatus: (id) => paymentsApiService.getConnectStatus(id),
        forceRefresh
      });
      setConnectStatus(status);
      const fallbackInfo =
        usedVenueFallback
          ? 'Stripe is connected for this venue. Status is shared for everyone with dashboard access (owners and admins).'
          : '';
      setInfo(cachedInfo || fallbackInfo);
    } catch (e) {
      const message = e?.message || 'Unable to load Stripe status.';
      setError(
        isAuthErrorMessage(message)
          ? formatStripeConnectLoadError(message)
          : isNetworkErrorMessage(message)
          ? getNetworkErrorMessage()
          : formatStripeConnectLoadError(message)
      );
    } finally {
      setLoading(false);
    }
  }, [venueId, venueDetails]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleConnectStripe = async () => {
    if (!venueId) return;
    if (!hasBackendToken()) {
      setError(getAuthErrorMessage());
      return;
    }

    setBusy(true);
    setError('');
    setInfo('');

    try {
      clearCachedStripeConnectStatus(venueId);
      const response = await paymentsApiService.startConnectOAuth(venueId, {
        returnUrl: connectReturnUrl,
        refreshUrl: connectReturnUrl
      });
      const redirectUrl = response?.url;
      if (!redirectUrl) {
        throw new Error('Stripe Connect URL missing from API response.');
      }
      window.location.assign(redirectUrl);
    } catch (e) {
      const message = e?.message || 'Failed to start Stripe Connect.';
      setError(
        isAuthErrorMessage(message)
          ? getAuthErrorMessage()
          : isNetworkErrorMessage(message)
          ? getNetworkErrorMessage()
          : message
      );
      setBusy(false);
    }
  };

  const handleDashboard = async () => {
    if (!venueId) return;
    if (!hasBackendToken()) {
      setError(getAuthErrorMessage());
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await paymentsApiService.createDashboardLink(venueId);
      const dashboardUrl = response?.url || response?.login_url;
      if (!dashboardUrl) throw new Error('Stripe dashboard link missing from API response.');
      window.open(dashboardUrl, '_blank', 'noopener,noreferrer');
      setBusy(false);
    } catch (e) {
      const message = e?.message || 'Unable to open Stripe dashboard.';
      setError(
        isAuthErrorMessage(message)
          ? getAuthErrorMessage()
          : isNetworkErrorMessage(message)
          ? getNetworkErrorMessage()
          : message
      );
      setBusy(false);
    }
  };

  const connectButtonLabel =
    summaryState === 'not_connected' ? 'Connect with Stripe' : 'Continue Stripe setup';

  return (
    <div className="stripe-connect-card">
      <div className="stripe-connect-card__header">
        <h4>Stripe payouts</h4>
        <span className={`stripe-connect-pill stripe-connect-pill--${summaryState}`}>
          {statusLabelMap[summaryState]}
        </span>
      </div>
      <p className="stripe-connect-card__hint">
        Connect your own Stripe account to receive organizer payments. Occasia does not create a
        Stripe account for you and does not store card or KYC data.
      </p>
      {connectStatus?.stripe_account_email || connectStatus?.email ? (
        <p className="stripe-connect-card__meta">
          Linked Stripe email: {connectStatus.stripe_account_email || connectStatus.email}
        </p>
      ) : null}
      {loading ? (
        <p className="stripe-connect-card__meta">Loading Stripe status...</p>
      ) : (
        <div className="stripe-connect-card__meta">
          <div>Charges enabled: {connectStatus?.charges_enabled ? 'Yes' : 'No'}</div>
          <div>Payouts enabled: {connectStatus?.payouts_enabled ? 'Yes' : 'No'}</div>
          {connectStatus?.stripe_account_id ? (
            <div>Account: {connectStatus.stripe_account_id}</div>
          ) : null}
        </div>
      )}
      {requirementsDue.length > 0 ? (
        <p className="stripe-connect-card__warn">
          Stripe still needs: {requirementsDue.join(', ')}
        </p>
      ) : null}
      {info ? <p className="stripe-connect-card__hint">{info}</p> : null}
      {error ? <p className="inline-error">{error}</p> : null}
      <div className="stripe-connect-card__actions">
        <button
          type="button"
          className="btn-primary"
          onClick={handleConnectStripe}
          disabled={busy || !venueId || !canManagePayments}
        >
          {busy ? 'Redirecting to Stripe…' : connectButtonLabel}
        </button>
        {summaryState !== 'not_connected' ? (
          <button
            type="button"
            className="btn-secondary"
            onClick={handleDashboard}
            disabled={busy || !venueId || !canManagePayments}
          >
            Open Stripe dashboard
          </button>
        ) : null}
        <button
          type="button"
          className="btn-secondary"
          onClick={() => loadStatus({ forceRefresh: true })}
          disabled={busy || loading || !venueId || !canManagePayments}
        >
          Refresh status
        </button>
      </div>
    </div>
  );
};

export default StripeConnectCard;
